import { atom } from 'jotai';
import { atomWithObservable } from 'jotai/utils';
import { doc, onSnapshot } from 'firebase/firestore';
import { Observable } from 'rxjs';
import { db } from 'utils/firebase';
import {
  TActivityFeedMediaMapped,
  TActivityFeedMediaWithIndex,
  UploadStatusEnum,
} from 'redux-store/api/activity-feed';
import { getProjectMedia } from 'utils/services/fileCabinetService';
import type { IProjectMediaItem } from 'redux-store/api/file-cabinet';
import { selectedProjectAtom } from 'atoms/projects';
import { selectedCompanyIdAtom } from 'atoms/company';

const PAGE_SIZE = 200;

const rawProjectMediaAtom = atom<IProjectMediaItem[]>([]);
const projectMediaTotalAtom = atom<number>(0);
const projectMediaPageAtom = atom<number>(1);
const projectMediaLoadingAtom = atom<boolean>(false);
const projectMediaRequestInFlightAtom = atom<boolean>(false);
const projectMediaFiltersAtom = atom<{
  fromDate?: string;
  toDate?: string;
  phaseId?: number;
  scheduleItemId?: number;
  userId?: number;
  type?: string;
  tags?: string[];
}>({});

export const isProjectMediaLoadingAtom = atom((get) => get(projectMediaLoadingAtom));
export const isProjectMediaRequestInFlightAtom = atom((get) =>
  get(projectMediaRequestInFlightAtom)
);
export const hasMoreProjectMediaAtom = atom((get) => {
  const total = get(projectMediaTotalAtom);
  const loaded = get(rawProjectMediaAtom).length;
  return total > 0 && loaded < total;
});
export const setProjectMediaFiltersAtom = atom(
  null,
  (
    _get,
    set,
    filters: {
      fromDate?: string;
      toDate?: string;
      phaseId?: number;
      scheduleItemId?: number;
      userId?: number;
      type?: string;
      tags?: string[];
    }
  ) => {
    set(projectMediaFiltersAtom, filters);
  }
);

/**
 * Loads project media from PostgreSQL via the unified File Cabinet API.
 * Pass `true` to reset pagination, otherwise loads the next page.
 */
export const loadProjectMediaAtom = atom(null, async (get, set, reset?: boolean) => {
  const project = get(selectedProjectAtom);
  const companyId = get(selectedCompanyIdAtom);
  const filters = get(projectMediaFiltersAtom);
  if (!project?.id || !companyId) return;
  if (get(projectMediaRequestInFlightAtom)) return;
  if (!reset && !get(hasMoreProjectMediaAtom)) return;

  const page = reset ? 1 : get(projectMediaPageAtom);
  const hasExistingData = get(rawProjectMediaAtom).length > 0;

  set(projectMediaRequestInFlightAtom, true);
  if (!hasExistingData) {
    set(projectMediaLoadingAtom, true);
  }

  try {
    const response = await getProjectMedia(project.id, {
      page,
      limit: PAGE_SIZE,
      ...filters,
    });

    if (reset) {
      const currentItems = get(rawProjectMediaAtom);
      const isUnchanged =
        currentItems.length === response.items.length &&
        currentItems.every(
          (cur, i) =>
            cur.id === response.items[i].id &&
            cur.description === response.items[i].description &&
            !!cur.isPostLinked === !!response.items[i].isPostLinked &&
            JSON.stringify(cur.tags || []) === JSON.stringify(response.items[i].tags || [])
        );
      if (!isUnchanged) {
        set(rawProjectMediaAtom, response.items);
      }
      set(projectMediaPageAtom, 2);
    } else {
      set(rawProjectMediaAtom, (prev) => [...prev, ...response.items]);
      set(projectMediaPageAtom, page + 1);
    }
    set(projectMediaTotalAtom, response.total);
  } catch (err) {
    console.error('[ProjectMedia] Failed to load project media:', err);
  } finally {
    set(projectMediaRequestInFlightAtom, false);
    set(projectMediaLoadingAtom, false);
  }
});

export const resetProjectMediaAtom = atom(null, (_get, set) => {
  set(rawProjectMediaAtom, []);
  set(projectMediaPageAtom, 1);
  set(projectMediaTotalAtom, 0);
});

/**
 * Optimistically removes items from the local list by File Cabinet id.
 * Used for snappy UI updates after deletes without waiting for the next refresh.
 */
export const removeProjectMediaItemsAtom = atom(null, (_get, set, ids: number[]) => {
  if (!ids.length) return;
  const idSet = new Set(ids);
  set(rawProjectMediaAtom, (prev) => prev.filter((item) => !idSet.has(item.id)));
  set(projectMediaTotalAtom, (prev) => Math.max(0, prev - ids.length));
});

/**
 * Optimistically flips `isPostLinked` to `true` for the given File Cabinet ids
 * so the Media list locks them from re-selection right after a `file_link` is
 * created (e.g. publishing a post from the Media tab). The next backend refresh
 * will overwrite this with the authoritative value.
 */
export const markProjectMediaItemsAsLinkedAtom = atom(null, (_get, set, ids: number[]) => {
  if (!ids.length) return;
  const idSet = new Set(ids);
  set(rawProjectMediaAtom, (prev) =>
    prev.map((item) => (idSet.has(item.id) ? { ...item, isPostLinked: true } : item))
  );
});

export const updateProjectMediaDescriptionAtom = atom(
  null,
  (_get, set, payload: { fileCabinetId: number; description: string | null }) => {
    set(rawProjectMediaAtom, (prev) =>
      prev.map((item) =>
        item.id === payload.fileCabinetId ? { ...item, description: payload.description } : item
      )
    );
  }
);

export const addProjectMediaTagsAtom = atom(
  null,
  (_get, set, payload: { fileCabinetIds: number[]; tag: string }) => {
    if (!payload.fileCabinetIds.length || !payload.tag.trim()) return;
    const idSet = new Set(payload.fileCabinetIds);
    const tag = payload.tag.trim();
    const tagLower = tag.toLowerCase();

    set(rawProjectMediaAtom, (prev) =>
      prev.map((item) => {
        if (!idSet.has(item.id)) return item;
        const existing = item.tags || [];
        if (existing.some((t) => t.toLowerCase() === tagLower)) return item;
        return { ...item, tags: [...existing, tag] };
      })
    );
  }
);

export const mediaStreamTickerAtom = atomWithObservable<number>(
  (get) => {
    const project = get(selectedProjectAtom);
    const companyId = get(selectedCompanyIdAtom);
    if (!project?.id || !companyId) {
      return new Observable<number>((subscriber) => subscriber.next(0));
    }
    const tickerRef = doc(db, `companies/${companyId}/projects/${project.id}/mediaStream/tick`);
    return new Observable<number>((subscriber) => {
      subscriber.next(0);
      let hasReceivedFirstSnapshot = false;
      let changeCount = 0;
      const unsubscribe = onSnapshot(tickerRef, () => {
        if (!hasReceivedFirstSnapshot) {
          hasReceivedFirstSnapshot = true;
          return;
        }
        changeCount += 1;
        subscriber.next(changeCount);
      });
      return () => unsubscribe();
    });
  },
  { initialValue: 0 }
);

/**
 * Derived atom that filters PostgreSQL media to images/videos only and groups by date.
 * Output shape matches TActivityFeedMediaMapped so the existing Media tab UI works unchanged.
 */
export const projectMediaAtom = atom<TActivityFeedMediaMapped>((get) => {
  const items = get(rawProjectMediaAtom);

  const visualItems = items.filter((item) => {
    const mt = item.mimeType || '';
    return mt.startsWith('image/') || mt.startsWith('video/');
  });

  const mediaItems: TActivityFeedMediaWithIndex[] = visualItems.map((item, idx) => ({
    id: `fc_${item.id}`,
    fileCabinetId: item.id,
    url: item.firebaseUrl || '',
    name: item.name,
    mimeType: item.mimeType,
    description: item.description || null,
    location: item.location || null,
    timestamp: item.timestamp || null,
    tags: item.tags || [],
    options: item.thumbnailUrl
      ? { soundLevel: [], duration: '', previewUrl: item.thumbnailUrl }
      : null,
    status: UploadStatusEnum.COMPLETED,
    createdAt: new Date(item.createdAt),
    profile: {
      id: item.creatorProfileId || 0,
      userId: item.creatorProfileId || 0,
      firstName: item.profileFirstName || '',
      lastName: item.profileLastName || '',
      role: '',
      avatar: { id: '', url: item.profileAvatarUrl || '', name: 'avatar' },
    },
    index: idx,
    isPostLinked: item.isPostLinked || false,
  }));

  // Sort newest first
  mediaItems.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  mediaItems.forEach((item, idx) => {
    item.index = idx;
  });

  // Group by date for the existing UI
  const groupedByDate: Record<string, TActivityFeedMediaWithIndex[]> = {};
  mediaItems.forEach((item) => {
    const dateKey = item.createdAt.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    if (!groupedByDate[dateKey]) groupedByDate[dateKey] = [];
    groupedByDate[dateKey].push(item);
  });

  const groupedItems: [string, TActivityFeedMediaWithIndex[]][] = Object.entries(groupedByDate);

  return {
    items: groupedItems,
    meta: { totalItems: mediaItems.length },
  };
});
